/**
 * Canlı destek (live chat) tipleri — backend'deki LiveChatViewModels ile birebir eşleşir.
 *
 * Enum'lar iki farklı biçimde gelir:
 *   - REST üzerinden isim olarak ("Agent")  → MvcExtensions'daki JsonStringEnumConverter
 *   - SignalR üzerinden sayı olarak (1)     → hub protokolünde o converter kayıtlı değil
 * Bu yüzden her enum alanı okunmadan önce normalizeMessage/normalizeConversation'dan geçmeli.
 */

export type LiveChatStatus = "Bot" | "WaitingForAgent" | "Active" | "Resolved" | "Closed";

export type LiveChatTopic =
  | "Unspecified"
  | "OrderIssue"
  | "DeliveryIssue"
  | "MissingOrWrongItem"
  | "FoodQuality"
  | "PaymentOrRefund"
  | "PromoCode"
  | "AccountOrApp"
  | "Other";

export type LiveChatPriority = "Low" | "Normal" | "High" | "Urgent";

export type LiveChatSenderRole = "Customer" | "Agent" | "Bot" | "System" | "Driver";

export type LiveChatMessageType =
  | "Text"
  | "Image"
  | "System"
  | "OrderCard"
  | "QuickReply"
  | "RatingRequest";

export type LiveChatChannel = "CustomerApp" | "DriverApp" | "Web";

export interface ILiveChatMessage {
  id: string;
  conversationId: string;
  senderId?: string | null;
  senderRole: LiveChatSenderRole;
  senderName?: string | null;
  messageType: LiveChatMessageType;
  content: string;
  /** Mutlak S3 URL'i — S3_IMAGE_BASE_URL ile birleştirilmez. */
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentSizeBytes?: number | null;
  metadataJson?: string | null;
  clientMessageId?: string | null;
  sentAt: string;
  readAt?: string | null;
  isRead: boolean;
}

export interface ILiveChatOrderContext {
  orderId: string;
  orderNo?: string | null;
  orderDate: string;
  orderPrice: number;
  statusName?: string | null;
  restaurantName?: string | null;
  estimatedDeliveryAt?: string | null;
  isCancelled: boolean;
}

export interface ILiveChatQueueStatus {
  conversationId: string;
  /** 1 tabanlı sıra. Temsilci üstlendiğinde 0. */
  position: number;
  estimatedWaitMinutes: number;
  onlineAgentCount: number;
  agentsAvailable: boolean;
}

export interface ILiveChatQuickReply {
  id: string;
  title: string;
  content: string;
  topic?: LiveChatTopic | null;
  forRole: LiveChatSenderRole;
  languageCode: string;
  sortOrder: number;
  isActive: boolean;
  usageCount: number;
}

export interface ILiveChatConversation {
  id: string;
  conversationNo: string;
  customerId: string;
  customerName?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  orderId?: string | null;
  order?: ILiveChatOrderContext | null;
  topic: LiveChatTopic;
  status: LiveChatStatus;
  priority: LiveChatPriority;
  channel: LiveChatChannel;
  isBotHandled: boolean;
  unreadCount: number;
  createdAt: string;
  queuedAt?: string | null;
  assignedAt?: string | null;
  firstResponseAt?: string | null;
  lastMessageAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  rating?: number | null;
  ratingComment?: string | null;
  queue?: ILiveChatQueueStatus | null;
  suggestedReplies: ILiveChatQuickReply[];
  messages: ILiveChatMessage[];
}

export interface ILiveChatConversationSummary {
  id: string;
  conversationNo: string;
  customerId: string;
  customerName?: string | null;
  agentId?: string | null;
  agentName?: string | null;
  orderId?: string | null;
  topic: LiveChatTopic;
  status: LiveChatStatus;
  priority: LiveChatPriority;
  channel: LiveChatChannel;
  lastMessagePreview?: string | null;
  createdAt: string;
  queuedAt?: string | null;
  lastMessageAt?: string | null;
  unreadCount: number;
  waitingMinutes?: number | null;
  rating?: number | null;
}

export interface ILiveChatMessagePage {
  conversationId: string;
  messages: ILiveChatMessage[];
  /** Bir önceki sayfa için `before` parametresi. Başa gelindiğinde null. */
  nextCursor?: string | null;
  hasMore: boolean;
}

/** QuickReply balonunun metadataJson içeriği. */
export interface ILiveChatQuickReplyMetadata {
  options: string[];
}

/* ------------------------------------------------------------------ */
/* Wire normalizasyonu                                                 */
/* ------------------------------------------------------------------ */

const STATUS: Record<number, LiveChatStatus> = {
  0: "Bot",
  1: "WaitingForAgent",
  2: "Active",
  3: "Resolved",
  4: "Closed",
};

const TOPIC: Record<number, LiveChatTopic> = {
  0: "Unspecified",
  1: "OrderIssue",
  2: "DeliveryIssue",
  3: "MissingOrWrongItem",
  4: "FoodQuality",
  5: "PaymentOrRefund",
  6: "PromoCode",
  7: "AccountOrApp",
  99: "Other",
};

const PRIORITY: Record<number, LiveChatPriority> = {
  0: "Low",
  1: "Normal",
  2: "High",
  3: "Urgent",
};

const SENDER_ROLE: Record<number, LiveChatSenderRole> = {
  0: "Customer",
  1: "Agent",
  2: "Bot",
  3: "System",
  4: "Driver",
};

const MESSAGE_TYPE: Record<number, LiveChatMessageType> = {
  0: "Text",
  1: "Image",
  2: "System",
  3: "OrderCard",
  4: "QuickReply",
  5: "RatingRequest",
};

const CHANNEL: Record<number, LiveChatChannel> = {
  0: "CustomerApp",
  1: "DriverApp",
  2: "Web",
};

const toName = <T extends string>(value: unknown, map: Record<number, T>, fallback: T): T => {
  if (typeof value === "string") return value as T;
  if (typeof value === "number") return map[value] ?? fallback;
  return fallback;
};

/**
 * SignalR, DateTime'ı MVC'nin DateTimeConverter'ı olmadan serileştirdiği için saat
 * dilimi eki olmadan gelebilir. Böyle bir metni `new Date()` yerel saat sanar; UTC
 * olarak işaretleyip kaymayı önlüyoruz.
 */
const toUtcIso = (value: unknown): string => {
  if (typeof value !== "string" || value.length === 0) return new Date().toISOString();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return hasZone ? value : `${value}Z`;
};

const toUtcIsoOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? toUtcIso(value) : null;

export const normalizeMessage = (raw: ILiveChatMessage): ILiveChatMessage => ({
  ...raw,
  senderRole: toName(raw.senderRole, SENDER_ROLE, "System"),
  messageType: toName(raw.messageType, MESSAGE_TYPE, "Text"),
  sentAt: toUtcIso(raw.sentAt),
  readAt: toUtcIsoOrNull(raw.readAt),
});

export const normalizeConversation = (raw: ILiveChatConversation): ILiveChatConversation => ({
  ...raw,
  status: toName(raw.status, STATUS, "Bot"),
  topic: toName(raw.topic, TOPIC, "Unspecified"),
  priority: toName(raw.priority, PRIORITY, "Normal"),
  channel: toName(raw.channel, CHANNEL, "Web"),
  suggestedReplies: (raw.suggestedReplies ?? []).map((r) => ({
    ...r,
    topic: r.topic == null ? null : toName(r.topic, TOPIC, "Unspecified"),
    forRole: toName(r.forRole, SENDER_ROLE, "Customer"),
  })),
  messages: (raw.messages ?? []).map(normalizeMessage),
});

export const normalizeSummary = (
  raw: ILiveChatConversationSummary
): ILiveChatConversationSummary => ({
  ...raw,
  status: toName(raw.status, STATUS, "Bot"),
  topic: toName(raw.topic, TOPIC, "Unspecified"),
  priority: toName(raw.priority, PRIORITY, "Normal"),
  channel: toName(raw.channel, CHANNEL, "Web"),
});

/** QuickReply balonundaki dokunulabilir seçenekler. */
export const parseQuickReplyOptions = (metadataJson?: string | null): string[] => {
  if (!metadataJson) return [];
  try {
    const parsed = JSON.parse(metadataJson) as Partial<ILiveChatQuickReplyMetadata>;
    return Array.isArray(parsed.options) ? parsed.options.filter((o) => typeof o === "string") : [];
  } catch {
    return [];
  }
};

/** OrderCard balonundaki sipariş özeti. */
export const parseOrderCard = (metadataJson?: string | null): ILiveChatOrderContext | null => {
  if (!metadataJson) return null;
  try {
    return JSON.parse(metadataJson) as ILiveChatOrderContext;
  } catch {
    return null;
  }
};

/** Sohbet kapalıysa yazma alanı kapatılır. */
export const isConversationWritable = (status: LiveChatStatus): boolean => status !== "Closed";

/** Puanlama yalnızca Resolved/Closed sohbetlerde ve bir kez yapılabilir. */
export const canRateConversation = (conversation: ILiveChatConversation | null): boolean =>
  !!conversation &&
  (conversation.status === "Resolved" || conversation.status === "Closed") &&
  conversation.rating == null;
